/**
 * Pincode → delivery date, for blocks/maahi-delivery-check.liquid.
 *
 * Two sources, chosen by the block's "source" setting and read from data-mode:
 *
 *   estimate    (default) — a purely local calculation: today + min_days to
 *                today + max_days. No network call, no credentials, nothing to
 *                configure. This is what the merchant asked for: an honest
 *                window rather than a promise the courier has not made.
 *   shiprocket  — the real per-destination date from Shiprocket, via the Apps
 *                Script Web App that holds the credentials. Parked since
 *                4 Sep 2026 waiting on a Shiprocket API user; the code path is
 *                kept intact so switching the setting is the only step needed.
 *
 * The Shiprocket request is deliberately a bare GET with a query string and NO
 * custom headers: Apps Script cannot answer a CORS preflight, so anything that
 * provokes one (custom headers, a JSON content-type POST) fails in the browser
 * even though it works from curl. Do not "improve" this to a POST.
 *
 * Listeners are delegated from document because the product page re-renders its
 * blocks on a variant change — a handler bound directly to the button would be
 * thrown away with the old markup and the button would go dead.
 *
 * PRESENTATIONAL ONLY. This reads a pincode and prints a date. It never touches
 * the cart, the checkout or the Razorpay path.
 */
(function () {
  'use strict';

  var STORE_KEY = 'maahi_last_pincode';

  /* Indian pincodes are six digits and never start with 0. */
  var PIN_RE = /^[1-9][0-9]{5}$/;

  function pinOf(root) {
    var input = root.querySelector('[data-delivery-pin]');
    return input ? (input.value || '').replace(/\D/g, '') : '';
  }

  function textEl(root) {
    return root.querySelector('[data-delivery-text]');
  }

  /* Replaces the result line. Nodes rather than innerHTML so the bold dates can
     never be a markup-injection route, even though every string here is built
     from a Date object rather than from anything the shopper typed. */
  function show(root, nodes, tone) {
    var el = root.querySelector('[data-delivery-result]');
    var slot = textEl(root);
    if (!el || !slot) return;

    while (slot.firstChild) slot.removeChild(slot.firstChild);
    for (var i = 0; i < nodes.length; i++) {
      slot.appendChild(typeof nodes[i] === 'string' ? document.createTextNode(nodes[i]) : nodes[i]);
    }

    el.className = 'maahi-delivery__result' + (tone ? ' maahi-delivery__result--' + tone : '');
    el.hidden = false;
  }

  function bold(text) {
    var s = document.createElement('strong');
    s.textContent = text;
    return s;
  }

  /* Built from local Y/M/D parts, never from a bare ISO string: `new Date("2026-09-11")`
     is read as UTC and slides to the previous day for anyone west of Greenwich. */
  function addDays(n) {
    var now = new Date();
    var d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    d.setDate(d.getDate() + n); /* rolls month and year over on its own */
    return d;
  }

  /* Short weekday, full month: "Fri, 11 September". The weekday is the part a
     shopper scans for, and abbreviating it keeps a two-date range on one line
     in the 429px product info column. */
  function longDate(d) {
    try {
      return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'long' });
    } catch (e) {
      return d.getDate() + '/' + (d.getMonth() + 1) + '/' + d.getFullYear();
    }
  }

  function isoDate(iso) {
    var p = String(iso).split('-');
    if (p.length !== 3) return null;
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    return isNaN(d.getTime()) ? null : d;
  }

  function intAttr(root, name, fallback) {
    var v = parseInt(root.getAttribute(name), 10);
    return isNaN(v) ? fallback : v;
  }

  /* The button is dead until the pincode is a real one, so "Check" can never be
     pressed on a half-typed number and return an answer for nowhere. */
  function syncButton(root) {
    var button = root.querySelector('[data-delivery-check]');
    if (!button || button.hasAttribute('data-busy')) return;
    button.disabled = !PIN_RE.test(pinOf(root));
  }

  /* Clears a stale answer as soon as the pincode is edited — otherwise the date
     for the previous pincode sits there looking like the answer for this one. */
  function clearResult(root) {
    var el = root.querySelector('[data-delivery-result]');
    if (el) el.hidden = true;
  }

  function estimate(root) {
    var min = intAttr(root, 'data-min-days', 4);
    var max = intAttr(root, 'data-max-days', 8);
    if (max < min) max = min;

    var lead = root.getAttribute('data-lead') || 'Estimated delivery between ';
    var join = root.getAttribute('data-join') || ' and ';
    var first = longDate(addDays(min));
    var last = longDate(addDays(max));

    /* Equal min and max would otherwise read "between Fri, 11 September and
       Fri, 11 September". */
    var nodes = first === last
      ? [lead, bold(first)]
      : [lead, bold(first), join, bold(last)];

    show(root, nodes, 'ok');
  }

  function fromShiprocket(root, pin) {
    var endpoint = root.getAttribute('data-endpoint');
    var button = root.querySelector('[data-delivery-check]');
    if (!endpoint) {
      estimate(root); /* never leave the shopper with a dead button */
      return;
    }

    if (button) {
      button.setAttribute('data-busy', '');
      button.disabled = true;
    }
    show(root, ['Checking…'], 'soft');

    var url = endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + 'pin=' + encodeURIComponent(pin);

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data || !data.ok) {
          /* Deliberately vague rather than inventing a date. A shopper who is
             told "we could not check" will try again; one shown a wrong date
             finds out days later. */
          show(root, ['We could not check delivery just now. Please try again in a moment.'], 'soft');
          return;
        }
        if (!data.serviceable) {
          show(root, ['Sorry, we do not deliver to ' + pin + ' yet.'], 'no');
          return;
        }
        var when = isoDate(data.deliver_by);
        var nodes = when
          ? ['Estimated delivery by ', bold(longDate(when))]
          : ['Estimated delivery in about ' + data.days + ' days'];
        if (data.cod) nodes.push(' · Cash on Delivery available');
        show(root, nodes, 'ok');
      })
      .catch(function () {
        show(root, ['We could not check delivery just now. Please try again in a moment.'], 'soft');
      })
      .then(function () {
        if (button) {
          button.removeAttribute('data-busy');
          syncButton(root);
        }
      });
  }

  function check(root) {
    var pin = pinOf(root);
    if (!PIN_RE.test(pin)) {
      show(root, ['Please enter a valid 6-digit pincode.'], 'no');
      return;
    }

    try { localStorage.setItem(STORE_KEY, pin); } catch (e) { /* private mode */ }

    if (root.getAttribute('data-mode') === 'shiprocket') fromShiprocket(root, pin);
    else estimate(root);
  }

  function rootOf(el) {
    return el && el.closest ? el.closest('[data-maahi-delivery]') : null;
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-delivery-check]');
    if (!trigger || trigger.disabled) return;
    var root = rootOf(trigger);
    if (root) check(root);
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest && event.target.closest('[data-delivery-pin]');
    if (!input) return;
    /* Digits only, so a pasted "201009 " or "20-1009" still enables the button. */
    var cleaned = (input.value || '').replace(/\D/g, '').slice(0, 6);
    if (cleaned !== input.value) input.value = cleaned;
    var root = rootOf(input);
    if (root) {
      syncButton(root);
      clearResult(root);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target.closest && event.target.closest('[data-delivery-pin]');
    if (!input) return;
    event.preventDefault();
    var root = rootOf(input);
    if (root && PIN_RE.test(pinOf(root))) check(root);
  });

  /* Restores the pincode this visitor last used, which is nearly always the one
     they want, and puts the button into the right state for it. In shiprocket
     mode an unconfigured block is hidden rather than shown with a button that
     fails on every press; in estimate mode there is nothing to configure. */
  function prepare() {
    var roots = document.querySelectorAll('[data-maahi-delivery]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];

      if (root.getAttribute('data-mode') === 'shiprocket' && !root.getAttribute('data-endpoint')) {
        root.hidden = true;
        continue;
      }
      root.hidden = false;

      var input = root.querySelector('[data-delivery-pin]');
      if (input && !input.value) {
        try {
          var saved = localStorage.getItem(STORE_KEY);
          if (saved && PIN_RE.test(saved)) input.value = saved;
        } catch (e) { /* private mode */ }
      }
      syncButton(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prepare);
  } else {
    prepare();
  }

  /* The product page swaps its blocks on a variant change, which brings in a
     fresh, unprepared copy of this markup. */
  if (window.MutationObserver) {
    new MutationObserver(prepare).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
